// GroupsIsland.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './GroupsIsland.css';
import GroupDetailIsland from './GroupDetailIsland/GroupDetailIsland';
import { createGroup, listGroups } from '../../../../api/groupApi';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import Input from '../../../../components/Input/Input';
import { Plus } from 'lucide-react';

function Modal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div className="tm-modal-backdrop" onMouseDown={onClose}>
      <div className="tm-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tm-modal-header">
          <div className="tm-modal-title">{title}</div>
          <button className="tm-icon-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="tm-modal-body">{children}</div>
      </div>
    </div>
  );
}

const TYPE_OPTIONS = [
  { label: 'Classroom', value: 'classroom' },
  { label: 'Team', value: 'team' },
  { label: 'Program', value: 'program' },
  { label: 'Cohort', value: 'cohort' },
  { label: 'Department', value: 'department' },
];

function formatType(t) {
  if (!t) return '—';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export default function GroupsIsland({ organizationId, currentUserId }) {
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('all'); // all | owned | member
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');

  // Create modal form
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupType, setNewGroupType] = useState('classroom');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErrMsg('');
      try {
        const data = await listGroups(organizationId);
        if (!alive) return;
        setGroups(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setErrMsg(e?.response?.data?.error || e?.message || 'Failed to load groups');
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    if (organizationId) load();
    return () => {
      alive = false;
    };
  }, [organizationId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
  
    return groups
      .filter((g) => {
        if (scope === "owned") {
          return currentUserId && Number(g.created_by) === Number(currentUserId);
        }
  
        if (scope === "member") {
          // ✅ Proper membership (including admins)
          return !!g.myRole;
        }
  
        return true;
      })
      .filter((g) => {
        if (!q) return true;
        return (
          (g.name || "").toLowerCase().includes(q) ||
          (g.type || "").toLowerCase().includes(q) ||
          (g.ownerName || "").toLowerCase().includes(q)
        );
      });
  }, [groups, query, scope, currentUserId]);
  

  const hasAnyGroups = groups.length > 0;
  const openCreate = () => {
    setNewGroupName('');
    setNewGroupType('classroom');
    setIsCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    setCreating(true);
    setErrMsg('');

    try {
      const res = await createGroup(organizationId, {
        name: newGroupName.trim(),
        type: newGroupType, // must be lowercase: classroom/team/cohort/program/department
      });

      // Your controller returns: { message, group: {...} }
      const created = res?.group;

      // Add to top optimistically (or refetch listGroups)
      if (created?.id) {
        setGroups((prev) => [
          {
            id: created.id,
            name: created.name,
            type: created.type,
            created_by: created.createdBy,
            created_at: created.createdAt,
            ownerName: created.ownerName,
            memberCount: 1,
          },
          ...prev,
        ]);
      }

      setIsCreateOpen(false);
    } catch (e2) {
      setErrMsg(e2?.response?.data?.error || e2?.message || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const openGroup = (gid) => {
    navigate(`/org/${organizationId}/management?tab=groups&groupId=${gid}&view=members`);
  };

  if (groupId) {
    return (
      <GroupDetailIsland
        organizationId={organizationId}
        groupId={Number(groupId)}
        currentUserId={currentUserId}
      />
    );
  }

  return (
    <div className="tm-island">
      <div className="tm-island-header">
        <div>
          <h2 className="tm-title">Groups</h2>
        </div>

        <button className="tm-primary-btn" onClick={openCreate}>
          <Plus size={24} /> New Group
        </button>
      </div>

      {!loading && hasAnyGroups && (
  <div className="tm-controls">

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search groups…"
        className="tm-input"
      />


    <div className="tm-chips">
      <button
        className={`tm-chip ${scope === 'all' ? 'active' : ''}`}
        onClick={() => setScope('all')}
      >
        All
      </button>

      <button
        className={`tm-chip ${scope === 'owned' ? 'active' : ''}`}
        onClick={() => setScope('owned')}
        disabled={!currentUserId}
        title={!currentUserId ? 'currentUserId not available' : ''}
      >
        Owned
      </button>

      <button
        className={`tm-chip ${scope === 'member' ? 'active' : ''}`}
        onClick={() => setScope('member')}
        disabled={!currentUserId}
        title={!currentUserId ? 'currentUserId not available' : ''}
      >
        Member
      </button>
    </div>
  </div>
)}


      {!!errMsg && <div className="tm-empty-subtitle" style={{ marginBottom: 12 }}>{errMsg}</div>}

      {loading ? (
        <div className="tm-empty">
          <div className="tm-empty-title">Loading groups…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="tm-empty">
          <div className="tm-empty-title">No groups found</div>
          <div className="tm-empty-subtitle">
            Create a group to start organizing practice sessions by team, class, or cohort.
          </div>
          <button className="tm-primary-btn" onClick={openCreate}>
            + Create your first group
          </button>
        </div>
      ) : (
        <div className="tm-grid">
          {filtered.map((g) => {
const isAdmin = g.myRole === 'admin';
            return (
              <button key={g.id} className="tm-card" onClick={() => openGroup(g.id)}>
                <div className="tm-card-top">
                  <div className="tm-card-title">{g.name}</div>
                  <div className="tm-card-arrow">→</div>
                </div>

                <div className="tm-card-meta">
                  <span className="tm-pill">{formatType(g.type)}</span>
                  <span className="tm-dot">•</span>
                  <span className="tm-muted">{g.memberCount ?? 0} members</span>
                </div>

                <div className="tm-card-bottom">
                  <div className="tm-muted">Owner: {g.ownerName || '—'}</div>
                  <div className={`tm-badge ${isAdmin ? 'badge-admin' : 'badge-member'}`}>
                    {isAdmin ? 'Admin' : 'Member'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Modal open={isCreateOpen} title="Create Group" onClose={() => setIsCreateOpen(false)}>
        <form onSubmit={handleCreate} className="tm-form"> 
        <label className="tm-label">Group Type</label>
<Dropdown
  options={TYPE_OPTIONS}
  value={newGroupType}
  onChange={(val) => setNewGroupType(val)}
  placeholder="Select group type…"
  ariaLabel="Group type"
/>
          <label className="tm-label">Group Name</label>
          <Input
            className="tm-input"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g., CS 401 – Interview Skills, HR Team"
            required
          />

        

          <div className="tm-form-actions">
            <button type="button" className="tm-secondary-btn" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="tm-primary-btn" disabled={creating}>
              {creating ? 'Creating…' : 'Create Group'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
